import { Box, Chip, type ChipTypeMap } from '@mui/material'
import Autocomplete, {
	type AutocompleteProps
} from '@mui/material/Autocomplete'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'

import './style.scss'

import makeStyles from '@mui/styles/makeStyles'
import clsx from 'clsx'
import {
	type ElementType,
	type ReactNode,
	useEffect,
	useId,
	useState
} from 'react'
import { useSwitcher } from 'shared/hooks'

import ArrowIcon from '../../../assets/image/icons/svg/arrow-down-grey.svg'
import Tag from '../Tag'

interface TagDatum {
	key: number
	text: string
}

/**
 * @deprecated
 */
export function DropdownSimple<
	Value,
	Multiple extends boolean | undefined,
	DisableClearable extends boolean | undefined,
	FreeSolo extends boolean | undefined,
	ChipComponent extends ElementType = ChipTypeMap['defaultComponent']
>({
	customIcon,
	dataForAuto,
	fromTeams,
	label,
	options,
	placeholder,
	tags = false,
	...props
}: AutocompleteProps<
	Value,
	Multiple,
	DisableClearable,
	FreeSolo,
	ChipComponent
> & {
	customIcon: ReactNode
	dataForAuto: any
	fromTeams?: boolean
	getOptions?: (param: any | void) => Promise<Value[]>
	id: string
	label: ReactNode
	onTagClick?: (param: any) => void
	placeholder: string
	tags?: boolean
}) {
	const [open, setOpen] = useState(false)
	const [loading, setLoading] = useState(false)
	const classes = useStyles(fromTeams)
	const id = useId()
	const [inputVal, setInputVal] = useState<null | Value | Value[]>(null)

	const getOptions = async () => {
		setOpen(true)
		setLoading(true)
		if (options.length === 0 || dataForAuto) {
			// @ts-ignore
			await props.getOptions?.()
		}
		//await props.getOptions({ take: 10 });
		setLoading(false)
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: legacy
	useEffect(() => {
		let delayDebounceFn = Number.NaN
		setLoading(true)

		if (dataForAuto && inputVal !== null && open) {
			delayDebounceFn = window.setTimeout(async () => {
				await props.getOptions?.({
					...dataForAuto,
					keywords: `${inputVal}*`,
					take: 10
				})
				setLoading(false)
			}, 1000)
		} else {
			setLoading(false)
		}

		return () => clearTimeout(delayDebounceFn)
	}, [inputVal])

	const onInputChange = async (value: Value) => {
		if (dataForAuto) {
			setInputVal(value)
		}
	}

	return (
		<div className={fromTeams ? 'dropdown-simple-accent' : 'dropdown-simple'}>
			{label && (
				<label
					className='select-label dropdown-simple__select-label'
					htmlFor={id}
				>
					{label}
				</label>
			)}
			<Autocomplete
				autoHighlight
				blurOnSelect
				classes={classes}
				className={clsx(customIcon && 'custom-icon')}
				// @ts-ignore
				getOptionLabel={option => option.text}
				// @ts-ignore
				isOptionEqualToValue={(option, value) => option.key === value.key}
				loading={loading}
				{...props}
				id={id}
				onClose={() => {
					setOpen(false)
				}}
				// @ts-ignore
				onInputChange={(event, value, reason) => onInputChange(value)}
				onOpen={getOptions}
				open={open}
				options={loading ? [] : options}
				popupIcon={
					customIcon && (
						<img
							alt='dropdownicon'
							className={'dropdown-simple__icon'}
							src={ArrowIcon}
						/>
					)
				}
				renderInput={params => (
					<TextField
						{...params}
						margin='none'
						// @ts-ignore
						padding='none'
						placeholder={placeholder}
						slotProps={{
							input: {
								...params.InputProps,
								endAdornment: (
									<>
										{loading && <CircularProgress color='inherit' size={15} />}
										{params.InputProps.endAdornment}
									</>
								)
							}
						}}
						variant='outlined'
					/>
				)}
				renderOption={(props, option: any) => (
					<li {...props}>
						{option.color && (
							<Box
								component='li'
								sx={{
									backgroundColor: option.color,
									borderRadius: '50%',
									height: '8px',
									marginRight: '10px',
									minWidth: '8px',
									width: '8px'
								}}
							/>
						)}
						{option.img && (
							<Box
								alt='avatar'
								component='img'
								src={option.img}
								sx={{
									borderRadius: '50%',
									height: '30px',
									marginRight: '10px',
									width: '30px'
								}}
							/>
						)}
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								width: '400px'
							}}
						>
							{option.text}
							{option.text2 && (
								<Box
									component='div'
									sx={{
										color: 'var(--light-grey)',
										fontSize: '13px'
									}}
								>
									{option.text2}
								</Box>
							)}
						</div>
					</li>
				)}
				renderTags={(tagValue, getTagProps) =>
					tagValue.map((option, index) => (
						<Chip
							// @ts-ignore
							key={option.text}
							// @ts-ignore
							label={option.text}
							size='small'
							{...getTagProps({ index })}
						/>
					))
				}
				// @ts-ignore
				value={props.value}
			/>
			{props.multiple && tags && (
				<Tags
					// @ts-ignore
					onTagClick={item => props.onTagClick?.(props.id, item)}
					values={props.value as TagDatum[]}
				/>
			)}
		</div>
	)
}

function Tags({
	onTagClick,
	values
}: {
	onTagClick: (value: TagDatum) => void
	values: TagDatum[]
}) {
	const showAll = useSwitcher(false)

	const showed = showAll.isOn ? values : values.slice(0, 3)
	const showButton = values.length > 3 && !showAll.isOn

	return (
		<div className='dropdown-simple__tags'>
			{showed.map(item => (
				<Tag
					key={item.key}
					onClick={() => onTagClick(item)}
					text={item.text}
					type='big'
				/>
			))}

			{showButton && (
				<button
					className='dropdown-simple__show-all'
					onClick={showAll.on}
					type='button'
				>
					Show more
				</button>
			)}
		</div>
	)
}

const useStyles = makeStyles(() => ({
	input: {
		color: '#202020',
		fontSize: '14px !important',
		fontWeight: 'normal !important',
		lineHeight: '20px !important',
		padding: '0 !important'
	},
	inputRoot: {
		backgroundColor: '#ffffff',
		padding: '6px 50px 6px 13px !important'
	},
	noOptions: {
		color: '#202020 !important',
		fontSize: '14px !important',
		fontWeight: 'normal !important',
		lineHeight: '20px !important'
	},
	option: {
		color: '#202020 !important',
		fontSize: '14px !important',
		fontWeight: 'normal !important',
		lineHeight: '20px !important',
		padding: '6px 12px !important'
	},
	paper: {
		borderBottom: '1px solid #0cb39f',
		borderLeft: '1px solid #0cb39f',
		borderRadius: '4px',
		borderRight: '1px solid #0cb39f',
		boxShadow: 'none',
		color: '#202020 !important',
		fontSize: '14px !important',
		fontWeight: 'normal !important',
		lineHeight: '20px !important',
		margin: '0 !important',
		padding: '0 !important'
	},
	popper: {},
	tag: {
		display: 'none !important'
	}
}))

export default DropdownSimple
